/**
 * Plain-language glossary (Slice 1 of the guided-process rework). Keeps the
 * PRECISE product term as the `title` and explains it in 2–3 merchant-friendly,
 * honest sentences (`short`). Reused by {@link HelpTip}, empty states, and the
 * getting-started checklist so the same wording appears everywhere.
 */
export interface GlossaryEntry {
  /** The precise product term — stays the heading (decision: keep precise terms). */
  readonly title: string;
  /** 2–3 plain sentences, no jargon, honest TTQA voice. */
  readonly short: string;
}

export type GlossaryKey =
  | 'storeDomain'
  | 'domainVerification'
  | 'scan'
  | 'journey'
  | 'journeyHealth'
  | 'sensitiveAction'
  | 'potentialDefect'
  | 'falsePositive'
  | 'evidenceRetention'
  | 'byokKey'
  | 'testCases'
  | 'membersRoles';

export const glossary: Record<GlossaryKey, GlossaryEntry> = {
  storeDomain: {
    title: 'Store domain',
    short:
      'The web address of the storefront you want us to watch, like shop.example.com. We only look at this public site — never your code, servers, or databases. You tell us the domain once when you add your store.',
  },
  domainVerification: {
    title: 'Domain verification',
    short:
      "A quick one-time check that proves you own the store before we look at it. You'll add a small record we give you, and we confirm it. This keeps anyone from pointing us at a site they don't control.",
  },
  scan: {
    title: 'Scan',
    short:
      'An automated visit where we browse your store like a shopper and check that key paths still work. A scan looks and clicks, but never buys, changes, or deletes anything. You can run one anytime.',
  },
  journey: {
    title: 'Customer journey',
    short:
      'A path a real shopper takes, such as “search for a product, add it to the cart, and start checkout.” We discover these paths automatically so you can see the flows that matter most to your sales.',
  },
  journeyHealth: {
    title: 'Journey health',
    short:
      "A simple at-a-glance read on whether each customer journey is working. Green means it looked fine on the last check; anything else means we spotted something worth a look. It's a status, not a guarantee.",
  },
  sensitiveAction: {
    title: 'Sensitive action',
    short:
      'A step that would really change something — like placing an order, paying, or editing an account. We stop at these boundaries and never complete them, so testing your store never costs you money or creates real orders.',
  },
  potentialDefect: {
    title: 'Potential defect',
    short:
      'Something we noticed that looks wrong — a broken step, an error, or a page that didn’t load. We call it “potential” because a person should confirm it first. Nothing is reported as a real problem until you say so.',
  },
  falsePositive: {
    title: 'False positive',
    short:
      "A potential defect that turns out to be fine on closer look. When you mark something a false positive, you're teaching us what “normal” looks like for your store, so we flag it less in the future.",
  },
  evidenceRetention: {
    title: 'Evidence & retention',
    short:
      'When we spot something, we save proof — like a screenshot or a record of the steps — so you can see exactly what happened. You choose how long we keep this evidence; after that window it’s deleted automatically.',
  },
  byokKey: {
    title: 'Your AI key (BYOK)',
    short:
      'Some smart features use an AI model that you connect with your own key (“bring your own key”). You stay in control of usage and billing, and basic scanning works without any key at all.',
  },
  testCases: {
    title: 'Test cases',
    short:
      'Specific checks tied to a customer journey — for example, “the cart shows the right total.” They spell out what “working” means so results are clear and repeatable, not just a gut feel.',
  },
  membersRoles: {
    title: 'Members & roles',
    short:
      'The people you invite to your organization and what each can do. Roles range from full admins to view-only, so you can share results with your team while keeping sensitive actions limited to the right people.',
  },
};
